import type { PayloadAction } from "@reduxjs/toolkit";
import type { StoreProductSummary } from "schemas/product";
import type { OkendoNamespace } from "schemas/runtimeEnvironment";
import type {
  MetafieldEntityType,
  MetafieldKey,
  MetafieldNamespace,
  MetafieldType,
  ProductMetafieldMapping,
  StoreProduct,
  VariantMetafieldMapping,
} from "schemas/runtimeProduct";

import { createSelector, createSlice } from "@reduxjs/toolkit";
import merge from "lodash-es/merge";
import {
  DEFAULT_ACTIVE_CURRENCY,
  DEFAULT_ACTIVE_LANGUAGE,
  DEFAULT_ACTIVE_SHOPIFY_URL_ROOT,
  DEFAULT_MONEY_FORMAT,
  DEFAULT_MONEY_WITH_CURRENCY_FORMAT,
} from "replo-runtime/shared/liquid";

import { selectTemplateEditorStoreProduct } from "./template-reducer";

/**
 * Mapping which maps metafield namespace to a mapping of key -> type. Useful for
 * situations where we have metafield values but haven't fetched definitions yet, but
 * still need to know the type of the metafield.
 */
export type MetafieldsNamespaceKeyTypeMapping = Record<
  MetafieldEntityType,
  Record<MetafieldNamespace, Record<MetafieldKey, MetafieldType>>
>;

export interface CommerceState {
  /**
   * Currently known mapping of product ID -> metafield values mapping (namespace ->
   * key -> value). May be empty if we have not fetched metafield values yet.
   */
  productMetafieldValues: ProductMetafieldMapping;
  /**
   * Currently known mapping of variant ID -> metafield values mapping (namespace ->
   * key -> value). May be empty if we have not fetched metafield values yet.
   */
  variantMetafieldValues: VariantMetafieldMapping;
  /**
   * Currently known Shopify products, grouped by their id. Useful as a cache for when
   * we have to fetch products in different places and don't want to depend on query
   * caches. Also helps with implementing pagination for products, since we can
   * easily request 5 products at a time from the backend and those 5 can be the first
   * 5 we _don't_ have in this mapping.
   */
  products: Record<string | number, StoreProduct>;
  productSummaries: Record<StoreProductSummary["id"], StoreProductSummary>;

  activeCurrency: string;
  activeLanguage: string;
  moneyFormat: string | null;
  moneyWithCurrencyFormat: string | null;

  shopifyUrlRoot: string;
  okendoWidgetNamespace: OkendoNamespace | null;
}

const initialState: CommerceState = {
  variantMetafieldValues: {},
  productMetafieldValues: {},
  products: {},
  productSummaries: {},
  activeCurrency: DEFAULT_ACTIVE_CURRENCY,
  activeLanguage: DEFAULT_ACTIVE_LANGUAGE,
  moneyFormat: DEFAULT_MONEY_FORMAT,
  moneyWithCurrencyFormat: DEFAULT_MONEY_WITH_CURRENCY_FORMAT,
  shopifyUrlRoot: DEFAULT_ACTIVE_SHOPIFY_URL_ROOT,
  okendoWidgetNamespace: null,
};

const commerceSlice = createSlice({
  name: "commerce",
  initialState,
  reducers: {
    setProductData: (state, action: PayloadAction<StoreProduct[]>) => {
      action.payload.forEach((product) => {
        state.products[Number(product.id)] = product;
      });
    },
    resetProductData: (state) => {
      state.products = {};
      state.productSummaries = {};
    },
    setProductSummaries: (
      state,
      action: PayloadAction<StoreProductSummary[]>,
    ) => {
      action.payload.forEach((product) => {
        state.productSummaries[product.id] = product;
      });
    },
    setProductMetafieldValues: (
      state,
      action: PayloadAction<ProductMetafieldMapping>,
    ) => {
      state.productMetafieldValues = {
        ...state.productMetafieldValues,
        ...action.payload,
      };
    },
    setVariantsMetafieldValues: (
      state,
      action: PayloadAction<VariantMetafieldMapping>,
    ) => {
      Object.entries(action.payload).forEach(([variantId, values]) => {
        const key = Number.parseInt(variantId);
        state.variantMetafieldValues[key] = merge(
          // eslint-disable-next-line replo/no-merge-side-effects -- (Noah, 2026-07-20): scoped replacement for an existing broad lint exception.
          state.variantMetafieldValues[key],
          values,
        );
      });
    },
    setActiveLanguage: (state, action: PayloadAction<string | null>) => {
      state.activeLanguage = action.payload ?? DEFAULT_ACTIVE_LANGUAGE;
    },
    setActiveCurrency: (state, action: PayloadAction<string | null>) => {
      state.activeCurrency = action.payload ?? DEFAULT_ACTIVE_CURRENCY;
    },
    setMoneyFormats: (
      state,
      action: PayloadAction<{
        moneyFormat: string | null;
        moneyWithCurrencyFormat: string | null;
      }>,
    ) => {
      state.moneyFormat = action.payload.moneyFormat ?? DEFAULT_MONEY_FORMAT;
      state.moneyWithCurrencyFormat =
        action.payload.moneyWithCurrencyFormat ?? DEFAULT_MONEY_FORMAT;
    },
    setShopifyUrlRoot: (state, action: PayloadAction<string | null>) => {
      state.shopifyUrlRoot = action.payload ?? DEFAULT_ACTIVE_SHOPIFY_URL_ROOT;
    },
    setOkendoWidgetNamespace: (
      state,
      action: PayloadAction<OkendoNamespace | null>,
    ) => {
      state.okendoWidgetNamespace = action.payload;
    },
  },
});

type EditorRootState = { commerce: CommerceState };

export const selectProductMetafieldValues = (state: EditorRootState) => {
  return state.commerce.productMetafieldValues;
};

export const selectVariantMetafieldValues = (state: EditorRootState) => {
  return state.commerce.variantMetafieldValues;
};

export const selectProductData = (state: EditorRootState) => {
  return state.commerce.products;
};

export const selectProductSummaries = (state: EditorRootState) => {
  return state.commerce.productSummaries;
};

export const selectAllProducts = createSelector(
  selectProductData,
  (products) => {
    return Object.values(products);
  },
);

export type Locale = {
  activeCurrency: string;
  activeLanguage: string;
  moneyFormat: string | null;
  moneyWithCurrencyFormat: string | null;
};

export const selectLocaleData = createSelector(
  (state: EditorRootState) => state.commerce.activeCurrency,
  (state: EditorRootState) => state.commerce.activeLanguage,
  (state: EditorRootState) => state.commerce.moneyFormat,
  (state: EditorRootState) => state.commerce.moneyWithCurrencyFormat,
  (activeCurrency, activeLanguage, moneyFormat, moneyWithCurrencyFormat) => ({
    activeCurrency,
    activeLanguage,
    moneyFormat,
    moneyWithCurrencyFormat,
  }),
);

export const selectShopifyUrlRoot = (state: EditorRootState) => {
  return state.commerce.shopifyUrlRoot;
};

export const selectOkendoWidgetNamespace = (state: EditorRootState) => {
  return state.commerce.okendoWidgetNamespace;
};

export const selectProductResolutionDependencies = createSelector(
  selectLocaleData,
  selectTemplateEditorStoreProduct,
  (localeData, templateProduct) => {
    const {
      moneyFormat,
      moneyWithCurrencyFormat,
      activeCurrency: currencyCode,
      activeLanguage: language,
    } = localeData;
    return {
      currencyCode,
      moneyFormat,
      moneyWithCurrencyFormat,
      language,
      templateProduct: templateProduct ?? null,
      isEditor: true,
      isShopifyProductsLoading: false,
    };
  },
);

export const { reducer } = commerceSlice;
const { actions } = commerceSlice;

export const {
  resetProductData,
  setActiveLanguage,
  setActiveCurrency,
  setMoneyFormats,
  setOkendoWidgetNamespace,
  setProductData,
  setProductSummaries,
  setProductMetafieldValues,
  setShopifyUrlRoot,
  setVariantsMetafieldValues,
} = actions;
